import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import type { AccountService, AccountState } from '@/account/AccountService';
import {
  DEFAULT_CREST,
  PUSH_CAPS,
  crestFromRow,
  defaultHelmet,
  helmetFromRow,
  parseCrewOverview,
  parseCrewRace,
  parseFriendsOverview,
  parseSocialInbox,
  type Crest,
  type CrewOverview,
  type CrewRaceInvite,
  type FriendsOverview,
  type Helmet,
  type SocialInbox,
} from '@/social/crewLogic';
import type { BlockedPlayer, ReportContextId, ReportReasonId } from '@/social/moderation';
import { BUILD_TAG } from '@/config/version';
import { isNativeApp, nativeShims } from '@/native/shims';

/**
 * SocialService.ts — the RACE CREW + FRIENDS data layer (M140).
 *
 *  - Every call is one SECURITY DEFINER RPC (supabase/migrations/
 *    m140_race_crew_v1.sql). The server reads the player from the session
 *    JWT; nothing here ever names whose data to touch. Calls NEVER throw —
 *    a failure answers status 'offline' (or null) and the UI says so.
 *  - LIVE delivery: while signed in, one private Realtime channel
 *    'inbox:<uuid>' carries everything addressed to this player (crew race
 *    invites, crew invites, friend requests, roster changes). The database
 *    publishes into it (realtime.send); a row-level policy lets each player
 *    receive ONLY their own topic.
 *  - Realtime only reaches a CONNECTED client, so the truth is re-read with
 *    social_inbox() on every (re)subscribe and every app resume — a player
 *    who was away still gets the crew race invite.
 */

export type SocialEvent =
  | { type: 'crew_race'; race: CrewRaceInvite }
  | { type: 'crew_race_closed'; id: string }
  | { type: 'crew_invite'; crewId: string; crewName: string; inviter: string }
  | { type: 'crew_changed' }
  | { type: 'crew_removed'; crewName: string }
  | { type: 'friend_request'; userId: string; name: string }
  | { type: 'friend_added'; userId: string; name: string }
  | { type: 'inbox'; inbox: SocialInbox };

/** One status row from a write RPC (+ whatever it carried back). */
export interface SocialResult {
  status: string;
  name?: string;
  crewId?: string | null;
  code?: string;
}

const OFFLINE: SocialResult = { status: 'offline' };

/** M156: what call() answers when the server refused a SUSPENDED Racer ID
 *  (dr_uid raises 'account_suspended'). Writes turn it into the status
 *  'suspended'; reads treat it like any failure. */
const SUSPENDED = Symbol('suspended');

/** M156: one REPORT (see supabase/functions/_shared/report.ts). */
export interface ReportPayload {
  kind: 'player' | 'club';
  name: string;
  reason: ReportReasonId;
  context: ReportContextId;
  room?: string | null;
  details?: string | null;
}

type Row = Record<string, unknown>;
const first = (data: unknown): Row | null =>
  Array.isArray(data)
    ? ((data[0] as Row | undefined) ?? null)
    : data && typeof data === 'object'
      ? (data as Row)
      : null;
const s = (v: unknown): string => (typeof v === 'string' ? v : '');

export class SocialService {
  private channel: RealtimeChannel | null = null;
  private channelUser: string | null = null;
  private channelClient: SupabaseClient | null = null;
  private subscribing = false;
  private lastInboxAt = 0;
  private readonly listeners = new Set<(e: SocialEvent) => void>();

  constructor(private readonly account: AccountService) {
    // A phone coming back from the background missed whatever Realtime said
    // while the WebView slept — re-read the inbox (bounded: once per 4 s).
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.refreshInbox();
    });
  }

  on(fn: (e: SocialEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: SocialEvent): void {
    for (const fn of this.listeners) {
      try {
        fn(e);
      } catch (err) {
        console.warn('[social] listener failed', err);
      }
    }
  }

  // ---- live inbox ---------------------------------------------------------------

  /** Follow the account: subscribe on sign-in, resubscribe on a user switch,
   *  close on sign-out. Idempotent — Flow calls it on every state change. */
  onAccountState(st: AccountState): void {
    const uid = st.status === 'signedIn' ? st.userId : null;
    if (uid === this.channelUser) return;
    void this.closeChannel();
    if (uid) void this.openChannel(uid);
  }

  private async openChannel(uid: string): Promise<void> {
    if (this.subscribing) return;
    this.subscribing = true;
    try {
      const client = await this.account.socialClient();
      if (!client || this.account.getState().userId !== uid) return;
      // Private channels authorize with the session JWT (RLS on
      // realtime.messages) — hand the realtime socket the current token.
      await client.realtime.setAuth();
      const ch = client.channel(`inbox:${uid}`, { config: { private: true } });
      ch.on('broadcast', { event: '*' }, (msg: { event?: string; payload?: unknown }) =>
        this.onBroadcast(msg.event ?? '', msg.payload),
      );
      ch.subscribe((status) => {
        // (Re)joined — anything sent while we were away is in the tables.
        if (status === 'SUBSCRIBED') void this.refreshInbox(true);
      });
      this.channel = ch;
      this.channelUser = uid;
      this.channelClient = client;
    } catch (err) {
      console.warn('[social] inbox subscribe failed', err);
    } finally {
      this.subscribing = false;
    }
  }

  private async closeChannel(): Promise<void> {
    const ch = this.channel;
    const client = this.channelClient;
    this.channel = null;
    this.channelUser = null;
    this.channelClient = null;
    if (ch && client) {
      try {
        await client.removeChannel(ch);
      } catch {
        /* already gone */
      }
    }
  }

  private onBroadcast(event: string, payload: unknown): void {
    const p = (payload && typeof payload === 'object' ? payload : {}) as Row;
    switch (event) {
      case 'crew_race': {
        const race = parseCrewRace(p);
        if (race) this.emit({ type: 'crew_race', race });
        break;
      }
      case 'crew_race_closed':
        if (s(p.id)) this.emit({ type: 'crew_race_closed', id: s(p.id) });
        break;
      case 'crew_invite':
        this.emit({
          type: 'crew_invite',
          crewId: s(p.crew_id),
          crewName: s(p.crew_name),
          inviter: s(p.inviter),
        });
        break;
      case 'crew_changed':
        this.emit({ type: 'crew_changed' });
        break;
      case 'crew_removed':
        this.emit({ type: 'crew_removed', crewName: s(p.crew_name) });
        break;
      case 'friend_request':
        this.emit({ type: 'friend_request', userId: s(p.user_id), name: s(p.name) });
        break;
      case 'friend_added':
        this.emit({ type: 'friend_added', userId: s(p.user_id), name: s(p.name) });
        break;
      default:
        break; // a newer server's event this build doesn't know — ignore
    }
  }

  /** Re-read the inbox (open crew race + badge counts) and announce it. */
  async refreshInbox(force = false): Promise<SocialInbox | null> {
    const now = Date.now();
    if (!force && now - this.lastInboxAt < 4000) return null;
    this.lastInboxAt = now;
    const data = await this.read('social_inbox');
    if (data === undefined) return null;
    const inbox = parseSocialInbox(data);
    this.emit({ type: 'inbox', inbox });
    return inbox;
  }

  // ---- RPC plumbing ---------------------------------------------------------------

  /** One RPC. undefined = couldn't ask (signed out / offline / server error);
   *  SUSPENDED = the server refused a suspended Racer ID (M156). */
  private async call(
    fn: string,
    args?: Record<string, unknown>,
  ): Promise<unknown> {
    try {
      const client = await this.account.socialClient();
      if (!client) return undefined;
      const res = await client.rpc(fn, args);
      if (res.error) {
        if (/account_suspended/.test(res.error.message)) return SUSPENDED;
        console.warn(`[social] ${fn} failed`, res.error.message);
        return undefined;
      }
      return res.data;
    } catch (err) {
      console.warn(`[social] ${fn} threw`, err);
      return undefined;
    }
  }

  /** A read RPC: a suspended account reads as "couldn't ask". */
  private async read(fn: string, args?: Record<string, unknown>): Promise<unknown> {
    const data = await this.call(fn, args);
    return data === SUSPENDED ? undefined : data;
  }

  /** A write RPC returning a TABLE(status, …) or a bare status text. */
  private async write(fn: string, args?: Record<string, unknown>): Promise<SocialResult> {
    const data = await this.call(fn, args);
    if (data === SUSPENDED) return { status: 'suspended' };
    if (data === undefined) return OFFLINE;
    if (typeof data === 'string') return { status: data };
    const row = first(data);
    if (!row) return OFFLINE;
    return {
      status: s(row.status) || 'offline',
      name: s(row.nickname) || s(row.name) || s(row.new_captain) || undefined,
      crewId: s(row.crew_id) || null,
      code: s(row.code) || undefined,
    };
  }

  // ---- crew ---------------------------------------------------------------------------

  async crewOverview(): Promise<CrewOverview | null> {
    const data = await this.read('crew_overview');
    return data === undefined ? null : parseCrewOverview(data);
  }

  /** Live availability (true free / false taken-or-refused). null = can't ask. */
  async crewNamesAvailable(names: string[]): Promise<Record<string, boolean> | null> {
    if (names.length === 0) return {};
    const data = await this.read('crew_name_available', { p_names: names.slice(0, 8) });
    if (!Array.isArray(data)) return null;
    const out: Record<string, boolean> = {};
    for (const r of data as Row[]) out[s(r.name)] = r.available === true;
    return out;
  }

  /** Found a club (M142: with its crest in the same call — the server keeps
   *  create_crew ONE function, the crest arguments defaulted, so an M140
   *  client's name-only call still works). */
  createCrew(name: string, crest: Crest = DEFAULT_CREST): Promise<SocialResult> {
    return this.write('create_crew', {
      p_name: name,
      p_crest_icon: crest.icon,
      p_crest_primary: crest.primary,
      p_crest_secondary: crest.secondary,
    });
  }

  /** The captain re-picks the crest (CLUB SETTINGS). */
  setCrewCrest(crest: Crest): Promise<SocialResult> {
    return this.write('set_crew_crest', {
      p_icon: crest.icon,
      p_primary: crest.primary,
      p_secondary: crest.secondary,
    });
  }

  inviteToCrew(racerId: string): Promise<SocialResult> {
    return this.write('invite_to_crew', { p_name: racerId });
  }

  respondCrewInvite(
    crewId: string,
    accept: boolean,
    opts: { switchCrew?: boolean; block?: boolean } = {},
  ): Promise<SocialResult> {
    return this.write('respond_crew_invite', {
      p_crew: crewId,
      p_accept: accept,
      p_switch: opts.switchCrew === true,
      p_block: opts.block === true,
    });
  }

  async crewPreview(code: string): Promise<{
    status: string;
    crewId: string | null;
    name: string;
    members: number;
    captain: string;
    myCrew: string | null;
    crest: Crest;
  }> {
    const data = await this.read('crew_preview', { p_code: code });
    const row = first(data);
    if (!row) {
      return {
        status: 'offline',
        crewId: null,
        name: '',
        members: 0,
        captain: '',
        myCrew: null,
        crest: { ...DEFAULT_CREST },
      };
    }
    return {
      status: s(row.status) || 'offline',
      crewId: s(row.crew_id) || null,
      name: s(row.name),
      members: typeof row.members === 'number' ? row.members : 0,
      captain: s(row.captain),
      myCrew: s(row.my_crew) || null,
      crest: crestFromRow(row),
    };
  }

  joinCrewByCode(code: string, switchCrew = false): Promise<SocialResult> {
    return this.write('join_crew_by_code', { p_code: code, p_switch: switchCrew });
  }

  leaveCrew(): Promise<SocialResult> {
    return this.write('leave_crew');
  }

  removeMember(userId: string): Promise<SocialResult> {
    return this.write('remove_crew_member', { p_user: userId });
  }

  transferCaptain(userId: string): Promise<SocialResult> {
    return this.write('transfer_captain', { p_user: userId });
  }

  renameCrew(name: string): Promise<SocialResult> {
    return this.write('rename_crew', { p_name: name });
  }

  resetCrewCode(): Promise<SocialResult> {
    return this.write('reset_crew_code');
  }

  /** M142: the primary + secondary colour (CLUB_COLORS) and the design. The
   *  server also keeps the M140 colourway column in step for old clients. */
  setHelmet(h: Helmet): Promise<SocialResult> {
    return this.write('set_helmet_style', {
      p_primary: h.primary,
      p_secondary: h.secondary,
      p_design: h.design,
    });
  }

  // ---- crew races -----------------------------------------------------------------------

  /** Open a crew race (the room is already chosen client-side). 'open' hands
   *  back the race another member opened first — join that one instead. */
  async startCrewRace(opts: {
    visibility: 'private' | 'public';
    room: string;
    host: string;
    city: string;
    field: number;
    /** M142: 0 = a single race in `city`; 3-9 = a club tour. */
    tour: number;
  }): Promise<{ status: string; race: CrewRaceInvite | null }> {
    const data = await this.read('start_crew_race', {
      p_visibility: opts.visibility,
      p_room: opts.room,
      p_host: opts.host,
      p_city: opts.city,
      p_field: opts.field,
      p_tour: opts.tour,
    });
    const row = first(data);
    if (!row) return { status: 'offline', race: null };
    return { status: s(row.status) || 'offline', race: parseCrewRace(row.race) };
  }

  closeCrewRace(id: string): Promise<SocialResult> {
    return this.write('close_crew_race', { p_id: id });
  }

  // ---- friends ----------------------------------------------------------------------------

  async friendsOverview(): Promise<FriendsOverview | null> {
    const data = await this.read('friends_overview');
    return data === undefined ? null : parseFriendsOverview(data);
  }

  sendFriendRequest(racerId: string): Promise<SocialResult> {
    return this.write('send_friend_request', { p_name: racerId });
  }

  async friendPreview(code: string): Promise<{
    status: string;
    userId: string | null;
    name: string;
    helmet: Helmet;
    xp: number;
    isFriend: boolean;
  }> {
    const data = await this.read('friend_preview', { p_code: code });
    const row = first(data);
    if (!row) {
      return {
        status: 'offline',
        userId: null,
        name: '',
        helmet: defaultHelmet(code),
        xp: 0,
        isFriend: false,
      };
    }
    const userId = s(row.user_id) || null;
    return {
      status: s(row.status) || 'offline',
      userId,
      name: s(row.nickname),
      helmet: helmetFromRow(row, userId ?? s(row.nickname)),
      xp: typeof row.xp === 'number' ? row.xp : 0,
      isFriend: row.is_friend === true,
    };
  }

  addFriendByCode(code: string): Promise<SocialResult> {
    return this.write('add_friend_by_code', { p_code: code });
  }

  respondFriendRequest(userId: string, accept: boolean, block = false): Promise<SocialResult> {
    return this.write('respond_friend_request', {
      p_user: userId,
      p_accept: accept,
      p_block: block,
    });
  }

  removeFriend(userId: string): Promise<SocialResult> {
    return this.write('remove_friend', { p_user: userId });
  }

  // ---- report + block (M156, App Store Guideline 1.2) ---------------------------------------

  /** Send one REPORT to the report-player edge function. Never throws.
   *  status: 'ok' | 'slow_down' | 'sign_in' | 'offline'. The answer never
   *  says whether the name belongs to an account (computer racers wear pool
   *  handles — a report must not reveal which seats were AI). */
  async reportPlayer(p: ReportPayload): Promise<{ status: string; ref?: string }> {
    try {
      const client = await this.account.socialClient();
      if (!client) return { status: 'sign_in' };
      const { data, error } = await client.functions.invoke('report-player', {
        body: {
          kind: p.kind,
          name: p.name,
          reason: p.reason,
          context: p.context,
          room: p.room ?? null,
          details: p.details ?? null,
          platform: isNativeApp() ? `app · ${nativeShims.platform ?? 'native'}` : 'web',
          build: BUILD_TAG,
        },
      });
      if (error) {
        const code = (error as { context?: { status?: number } }).context?.status;
        if (code === 429) return { status: 'slow_down' };
        if (code === 401) return { status: 'sign_in' };
        console.warn('[social] report failed', error.message);
        return { status: 'offline' };
      }
      const row = (data ?? {}) as { ok?: boolean; ref?: string };
      return row.ok ? { status: 'ok', ref: row.ref } : { status: 'offline' };
    } catch (err) {
      console.warn('[social] report threw', err);
      return { status: 'offline' };
    }
  }

  /** Block a player by the name seen in a race, or by id (Racer Club rows).
   *  'ok' even for a name with no account (never reveals AI seats). */
  blockPlayer(target: { name?: string; userId?: string }): Promise<SocialResult> {
    return this.write('block_player', {
      p_name: target.userId ? null : (target.name ?? null),
      p_user: target.userId ?? null,
    });
  }

  unblockPlayer(userId: string): Promise<SocialResult> {
    return this.write('unblock_player', { p_user: userId });
  }

  /** The players this account blocked, by their current names. null = couldn't ask. */
  async myBlocks(): Promise<BlockedPlayer[] | null> {
    const data = await this.read('my_blocks');
    if (!Array.isArray(data)) return null;
    return (data as Row[])
      .map((r) => ({ userId: s(r.user_id) || null, name: s(r.name) }))
      .filter((b) => b.name !== '');
  }

  // ---- push tokens (iOS crew-race pings) ----------------------------------------------------

  registerPushToken(token: string, platform: 'ios' | 'android'): Promise<SocialResult> {
    // M142: p_caps says this app can open a club tour (crew-push pings tours
    // only to such tokens).
    return this.write('register_push_token', {
      p_token: token,
      p_platform: platform,
      p_caps: PUSH_CAPS,
    });
  }

  unregisterPushToken(token: string): Promise<SocialResult> {
    return this.write('unregister_push_token', { p_token: token });
  }

  /** Ask the crew-push edge function to ping the crew's iPhones about a race
   *  this player just opened (fire-and-forget; it no-ops until the owner's
   *  Apple push key is configured). */
  async pushCrewRace(raceId: string): Promise<void> {
    try {
      const client = await this.account.socialClient();
      if (!client) return;
      await client.functions.invoke('crew-push', { body: { race_id: raceId } });
    } catch {
      /* a push is a bonus — the realtime pop-up already went out */
    }
  }

  /** Sign-out tidy-up (AccountService.beforeSignOut): the live channel closes. */
  async shutdown(): Promise<void> {
    await this.closeChannel();
  }
}
